<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import AlertCircleIcon from '@/assets/svg/alert-circle-24.svg?component';
import CloseIcon from '@/assets/svg/close-24.svg?component';
import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import MeService from '@/core/shared/me/me.service';
import { getUserName } from '@/core/shared/user-profile/utils';

enum TransferTarget {
  TEAMMATE = 'teammate',
  TEAM = 'team',
}

// Props
// ===================
const props = defineProps<{
  isOpen: boolean;
  isLoading: boolean;
  copilotName: string;
  jobCount: number;
  /** Current owner's user profile id; null when the recruiter is already team-owned. */
  currentOwnerId: number | null;
}>();

// Emits
// ===================
const emit = defineEmits<{
  /** `ownerId` is the chosen teammate, or null to make the recruiter team-owned. */
  (e: 'transfer', ownerId: number | null): void;
  (e: 'cancel'): void;
  (e: 'after-leave'): void;
}>();

// Services
// ===================
const meService = new MeService();

// Reactive state
// ===================
const target = ref<TransferTarget>(TransferTarget.TEAMMATE);
const selectedTeammateId = ref<number | undefined>(undefined);

// Computed Properties
// ===================
const isModalOpen = computed(() => props.isOpen);
const isTeammateSelected = computed(() => target.value === TransferTarget.TEAMMATE);
const isTeamSelected = computed(() => target.value === TransferTarget.TEAM);
const isAlreadyTeamOwned = computed(() => props.currentOwnerId === null);

const teammates = computed(() => {
  const users = meService.employerUsers ?? [];

  return users
    .filter((user) => user.id !== props.currentOwnerId)
    .map((user) => ({ id: user.id, title: getUserName(user) || user.email }))
    .sort((a, b) => a.title.localeCompare(b.title));
});

const teammateDescription = computed(() =>
  meService.isEmployerAdmin
    ? "One teammate becomes the owner. You'll keep admin access."
    : 'Hand over ownership to another teammate',
);

const jobsWarningLead = computed(() => {
  const jobText = props.jobCount === 1 ? 'active job' : 'active jobs';
  return `This AI recruiter is assigned to ${props.jobCount} ${jobText}.`;
});

const canSubmit = computed(() => {
  if (isTeamSelected.value) {
    return !isAlreadyTeamOwned.value;
  }

  return selectedTeammateId.value !== undefined;
});

// Methods
// ===================
const selectTarget = (value: TransferTarget) => {
  if (value === TransferTarget.TEAM && isAlreadyTeamOwned.value) {
    return;
  }

  target.value = value;
};

const handleTeammateChange = (value?: string | number) => {
  const id = Number(value);
  selectedTeammateId.value = Number.isInteger(id) && id > 0 ? id : undefined;
};

const handleSubmit = () => {
  if (!canSubmit.value) {
    return;
  }

  emit('transfer', isTeamSelected.value ? null : (selectedTeammateId.value as number));
};

const resetSelection = () => {
  target.value = TransferTarget.TEAMMATE;
  selectedTeammateId.value = undefined;
};

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      resetSelection();
    }
  },
);
</script>

<template>
  <v-dialog
    :model-value="isModalOpen"
    max-width="576px"
    persistent
    @after-leave="emit('after-leave')"
  >
    <div
      class="shadow-6 rounded-2xl bg-white p-6 sm:p-8"
      data-test="copilot-personality-transfer-modal"
    >
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-6">
        <div>
          <h2 class="font-serif text-2xl font-black leading-[1.2] text-highlight-1000">
            Transfer ownership
          </h2>
          <p class="mt-2 text-base font-normal leading-6 text-shade-820">
            Decide who can edit and delete this AI recruiter.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          class="shrink-0 text-shade-900"
          data-test="copilot-personality-transfer-close"
          @click="emit('cancel')"
        >
          <CloseIcon class="h-6 w-6" />
        </button>
      </div>

      <!-- Target options -->
      <div
        role="radiogroup"
        :aria-label="`New owner of ${copilotName}`"
        class="flex flex-col gap-4"
      >
        <!-- Transfer to a teammate -->
        <div
          role="radio"
          tabindex="0"
          :aria-checked="isTeammateSelected"
          class="transfer-option"
          :class="{ 'transfer-option--selected': isTeammateSelected }"
          data-test="copilot-personality-transfer-option-teammate"
          @click="selectTarget(TransferTarget.TEAMMATE)"
          @keydown.enter.prevent="selectTarget(TransferTarget.TEAMMATE)"
          @keydown.space.prevent="selectTarget(TransferTarget.TEAMMATE)"
        >
          <p class="text-sm font-extrabold leading-6 text-black">Transfer to a teammate</p>
          <p class="text-sm font-normal leading-[21px] text-shade-820">
            {{ teammateDescription }}
          </p>
          <div v-if="isTeammateSelected" class="mt-2.5" @click.stop @keydown.stop>
            <label class="mb-0.5 block text-xs font-bold leading-[18px] text-shade-880">
              Transfer to
            </label>
            <!-- Standard outlined select, as in the job activation form (JobStateSelect) -->
            <v-autocomplete
              :model-value="selectedTeammateId"
              :items="teammates"
              :disabled="isLoading"
              :menu-icon="ChevronIcon"
              item-title="title"
              item-value="id"
              variant="outlined"
              density="compact"
              placeholder="Select a teammate"
              spellcheck="false"
              hide-details
              data-test="copilot-personality-transfer-teammate"
              @update:model-value="handleTeammateChange"
            />
          </div>
        </div>

        <!-- Make team-owned -->
        <div
          role="radio"
          :tabindex="isAlreadyTeamOwned ? -1 : 0"
          :aria-checked="isTeamSelected"
          :aria-disabled="isAlreadyTeamOwned"
          class="transfer-option"
          :class="{
            'transfer-option--selected': isTeamSelected,
            'cursor-not-allowed opacity-60': isAlreadyTeamOwned,
          }"
          data-test="copilot-personality-transfer-option-team"
          @click="selectTarget(TransferTarget.TEAM)"
          @keydown.enter.prevent="selectTarget(TransferTarget.TEAM)"
          @keydown.space.prevent="selectTarget(TransferTarget.TEAM)"
        >
          <p class="text-sm font-bold leading-6 text-black">Make team-owned</p>
          <p class="text-sm font-normal leading-[21px] text-shade-800">
            Any admin in your org can edit or delete this AI recruiter
          </p>
        </div>
      </div>

      <!-- Active jobs notice -->
      <div
        v-if="jobCount > 0"
        class="mt-6 flex items-center gap-2 rounded-lg bg-tint-40 p-4"
        data-test="copilot-personality-transfer-jobs-notice"
      >
        <AlertCircleIcon class="h-6 w-6 shrink-0" />
        <p class="text-sm font-normal leading-[21px] text-black">
          <span class="font-bold">{{ jobsWarningLead }}</span>
          Transferring ownership won't interrupt recruiting activity or change job settings.
        </p>
      </div>

      <!-- Actions: stack on mobile like the delete modal -->
      <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-4">
        <v-btn
          :ripple="false"
          class="transfer-cancel-button"
          variant="text"
          :disabled="isLoading"
          @click.prevent="emit('cancel')"
          >Cancel</v-btn
        >
        <v-btn
          :ripple="false"
          class="modal-button-primary"
          variant="flat"
          :loading="isLoading"
          :disabled="!canSubmit"
          data-test="copilot-personality-transfer-submit"
          @click.prevent="handleSubmit"
          >Transfer ownership</v-btn
        >
      </div>
    </div>
  </v-dialog>
</template>

<style lang="postcss" scoped>
.transfer-option {
  @apply w-full cursor-pointer rounded-xl border border-tint-80 bg-white p-4 text-left transition-colors;
}

.transfer-option--selected {
  @apply border-2 border-highlight-500 p-[15px];
  background-image: linear-gradient(
    109deg,
    rgba(174, 53, 247, 0.04) 0%,
    rgba(255, 255, 255, 0.04) 8%,
    rgba(240, 236, 252, 0.04) 34%,
    rgba(108, 64, 229, 0.04) 100%
  );
}

.transfer-cancel-button {
  @apply !h-[40px] !rounded-md !px-4 !text-sm !font-semibold !normal-case !tracking-[0.28px] !text-highlight-1000;
}
</style>
